import { useState, useEffect, useCallback, memo } from "react";
import Navbar from "@/components/Navbar";
import AddCaseDialog from "@/components/dialogs/AddCaseDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trash2, Edit, Upload, Download, FileText, Image as ImageIcon, FolderOpen } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CaseFormFields = memo(({ data, setData, isEdit = false }) => (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="title">عنوان القضية *</Label>
          <Input
            id="title"
            value={data.title}
            onChange={(e) => setData(prev => ({ ...prev, title: e.target.value }))}
            required
            data-testid="case-title-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="case_number">رقم القضية</Label>
          <Input
            id="case_number"
            value={data.case_number}
            onChange={(e) => setData(prev => ({ ...prev, case_number: e.target.value }))}
            data-testid="case-number-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="case_type">نوع القضية *</Label>
          <Select
            value={data.case_type}
            onValueChange={(value) => setData(prev => ({ ...prev, case_type: value }))}
          >
            <SelectTrigger id="case_type" data-testid="case-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="civil">مدني</SelectItem>
              <SelectItem value="criminal">جنائي</SelectItem>
              <SelectItem value="commercial">تجاري</SelectItem>
              <SelectItem value="labor">عمل</SelectItem>
              <SelectItem value="personal_status">أحوال شخصية</SelectItem>
              <SelectItem value="administrative">إداري</SelectItem>
              <SelectItem value="employee_judiciary">قضاء موظفين</SelectItem>
              <SelectItem value="investigation">تحقيق</SelectItem>
              <SelectItem value="misdemeanor">جنح</SelectItem>
              <SelectItem value="internal_security">قوى الأمن الداخلي</SelectItem>
              <SelectItem value="military_court">المحكمة العسكرية</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="status">حالة القضية *</Label>
          <Select
            value={data.status}
            onValueChange={(value) => setData(prev => ({ ...prev, status: value }))}
          >
            <SelectTrigger id="status" data-testid="case-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">نشطة</SelectItem>
              <SelectItem value="pending">قيد الانتظار</SelectItem>
              <SelectItem value="closed">مغلقة</SelectItem>
              <SelectItem value="archived">مؤرشفة</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="court_name">اسم المحكمة</Label>
        <Input
          id="court_name"
          value={data.court_name}
          onChange={(e) => setData(prev => ({ ...prev, court_name: e.target.value }))}
          data-testid="court-name-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="judge_name">اسم القاضي (اختياري)</Label>
          <Input
            id="judge_name"
            placeholder="مثال: القاضي أحمد محمد"
            value={data.judge_name}
            onChange={(e) => setData(prev => ({ ...prev, judge_name: e.target.value }))}
            data-testid="judge-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="my_party_lawyer">المحامي (اختياري)</Label>
          <Input
            id="my_party_lawyer"
            placeholder="مثال: المحامي علي حسن"
            value={data.my_party_lawyer}
            onChange={(e) => setData(prev => ({ ...prev, my_party_lawyer: e.target.value }))}
            data-testid="my-party-lawyer-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="plaintiff">المدعي</Label>
          <Input
            id="plaintiff"
            value={data.plaintiff}
            onChange={(e) => setData(prev => ({ ...prev, plaintiff: e.target.value }))}
            data-testid="plaintiff-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="defendant">المدعى عليه</Label>
          <Input
            id="defendant"
            value={data.defendant}
            onChange={(e) => setData(prev => ({ ...prev, defendant: e.target.value }))}
            data-testid="defendant-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">وصف القضية *</Label>
        <Textarea
          id="description"
          rows={4}
          value={data.description}
          onChange={(e) => setData(prev => ({ ...prev, description: e.target.value }))}
          required
          data-testid="description-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">ملاحظات</Label>
        <Textarea
          id="notes"
          rows={3}
          value={data.notes}
          onChange={(e) => setData(prev => ({ ...prev, notes: e.target.value }))}
          data-testid="notes-input"
        />
      </div>
    </>
));

const CasesNew = ({ user, onLogout }) => {
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    case_number: "",
    case_type: "civil",
    status: "active",
    court_name: "",
    judge_name: "",
    opponent_lawyer: "",
    plaintiff: "",
    defendant: "",
    description: "",
    notes: ""
  });
  const [uploadData, setUploadData] = useState({
    file: null,
    title: "",
    description: "",
    tags: ""
  });

  useEffect(() => {
    fetchCases();
  }, []);

  useEffect(() => {
    if (selectedCase) {
      fetchCaseDocuments(selectedCase.id);
    }
  }, [selectedCase]);

  const fetchCases = async () => {
    try {
      const response = await axios.get(`${API}/cases`);
      setCases(response.data);
    } catch (error) {
      toast.error("خطأ في تحميل القضايا");
    } finally {
      setLoading(false);
    }
  };

  const fetchCaseDocuments = async (caseId) => {
    try {
      const response = await axios.get(`${API}/documents`, {
        params: { case_id: caseId }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/cases/${formData.id}`, formData);
      toast.success("تم تحديث القضية بنجاح");
      setEditDialogOpen(false);
      if (selectedCase?.id === formData.id) {
        setSelectedCase({ ...formData });
      }
      fetchCases();
    } catch (error) {
      toast.error("خطأ في تحديث القضية");
    }
  };

  const openEditDialog = (caseItem) => {
    setFormData({ ...caseItem });
    setEditDialogOpen(true);
  };

  const handleDelete = async (caseId) => {
    if (!window.confirm("هل أنت متأكد من حذف هذه القضية؟")) return;
    
    try {
      await axios.delete(`${API}/cases/${caseId}`);
      toast.success("تم حذف القضية");
      if (selectedCase?.id === caseId) {
        setSelectedCase(null);
      }
      fetchCases();
    } catch (error) {
      toast.error("خطأ في حذف القضية");
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file || !selectedCase) return;

    const formData = new FormData();
    formData.append("file", uploadData.file);
    formData.append("title", uploadData.title || uploadData.file.name);
    formData.append("case_id", selectedCase.id);
    if (uploadData.description) formData.append("description", uploadData.description);
    if (uploadData.tags) formData.append("tags", uploadData.tags);

    try {
      await axios.post(`${API}/documents/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("تم رفع الملف بنجاح");
      setUploadDialogOpen(false);
      setUploadData({ file: null, title: "", description: "", tags: "" });
      fetchCaseDocuments(selectedCase.id);
    } catch (error) {
      toast.error("خطأ في رفع الملف");
    }
  };

  const handleDownload = async (documentId, fileName) => {
    try {
      const response = await axios.get(`${API}/documents/${documentId}/download`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("تم تحميل الملف بنجاح");
    } catch (error) {
      let errorMsg = "خطأ في تحميل الملف";
      if (error.response?.status === 410) {
        errorMsg = "هذا الملف غير متوفر، يرجى إعادة الرفع";
      } else if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          errorMsg = json.detail || errorMsg;
        } catch {}
      } else {
        errorMsg = error.response?.data?.detail || errorMsg;
      }
      toast.error(errorMsg);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا الملف؟")) return;
    
    try {
      await axios.delete(`${API}/documents/${documentId}`);
      toast.success("تم حذف الملف");
      fetchCaseDocuments(selectedCase.id);
    } catch (error) {
      toast.error("خطأ في حذف الملف");
    }
  };

  const getCaseTypeLabel = (type) => {
    const types = {
      civil: "مدني",
      criminal: "جنائي",
      commercial: "تجاري",
      labor: "عمل",
      personal_status: "أحوال شخصية",
      administrative: "إداري",
      employee_judiciary: "قضاء موظفين",
      investigation: "تحقيق",
      misdemeanor: "جنح",
      internal_security: "قوى الأمن الداخلي",
      military_court: "المحكمة العسكرية"
    };
    return types[type] || type;
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: "badge badge-green",
      pending: "badge badge-yellow",
      closed: "badge badge-red",
      archived: "badge badge-blue"
    };
    const labels = {
      active: "نشطة",
      pending: "قيد الانتظار",
      closed: "مغلقة",
      archived: "مؤرشفة"
    };
    return <span className={badges[status]}>{labels[status]}</span>;
  };

  const getFileIcon = (fileType) => {
    if (fileType === "image") return <ImageIcon className="w-5 h-5 text-blue-600" />;
    return <FileText className="w-5 h-5 text-green-600" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("ar-IQ", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8" data-testid="cases-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">إدارة القضايا</h1>
            <p className="text-lg text-slate-600">تنظيم ومتابعة جميع قضاياك مع الأرشيف المدمج</p>
          </div>
          
          <AddCaseDialog onSuccess={fetchCases} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cases List */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>القضايا ({cases.length})</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[calc(100vh-300px)] overflow-y-auto">
                {loading ? (
                  <div>جاري التحميل...</div>
                ) : cases.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">لا توجد قضايا</p>
                ) : (
                  cases.map((caseItem) => (
                    <div
                      key={caseItem.id}
                      onClick={() => setSelectedCase(caseItem)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        selectedCase?.id === caseItem.id
                          ? "border-blue-500 bg-blue-50"
                          : "border-slate-200 hover:border-blue-300 hover:bg-slate-50"
                      }`}
                      data-testid={`case-item-${caseItem.id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-slate-900">{caseItem.title}</h3>
                        {getStatusBadge(caseItem.status)}
                      </div>
                      <p className="text-sm text-slate-500">{getCaseTypeLabel(caseItem.case_type)}</p>
                      {caseItem.case_number && (
                        <p className="text-xs text-slate-400 mt-1">رقم: {caseItem.case_number}</p>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Case Details & Archive */}
          <div className="lg:col-span-2">
            {selectedCase ? (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{selectedCase.title}</CardTitle>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(selectedCase)}
                        className="gap-2"
                        data-testid="edit-case-button"
                      >
                        <Edit className="w-4 h-4" />
                        تعديل
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(selectedCase.id)}
                        className="gap-2"
                        data-testid="delete-case-button"
                      >
                        <Trash2 className="w-4 h-4" />
                        حذف
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="details" className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="details">تفاصيل القضية</TabsTrigger>
                      <TabsTrigger value="archive">
                        <FolderOpen className="w-4 h-4 ml-2" />
                        الأرشيف ({documents.length})
                      </TabsTrigger>
                    </TabsList>

                    {/* Details Tab */}
                    <TabsContent value="details" className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-slate-500">رقم القضية</p>
                          <p className="font-semibold">{selectedCase.case_number || "-"}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">الحالة</p>
                          {getStatusBadge(selectedCase.status)}
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">النوع</p>
                          <p className="font-semibold">{getCaseTypeLabel(selectedCase.case_type)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">المحكمة</p>
                          <p className="font-semibold">{selectedCase.court_name || "-"}</p>
                        </div>
                        {selectedCase.judge_name && (
                          <div>
                            <p className="text-sm text-slate-500">القاضي</p>
                            <p className="font-semibold">{selectedCase.judge_name}</p>
                          </div>
                        )}
                        {selectedCase.my_party_lawyer && (
                          <div>
                            <p className="text-sm text-slate-500">المحامي</p>
                            <p className="font-semibold">{selectedCase.my_party_lawyer}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-sm text-slate-500">المدعي</p>
                          <p className="font-semibold">{selectedCase.plaintiff || "-"}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">المدعى عليه</p>
                          <p className="font-semibold">{selectedCase.defendant || "-"}</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 mb-2">الوصف</p>
                        <p className="text-slate-700">{selectedCase.description}</p>
                      </div>
                      {selectedCase.notes && (
                        <div>
                          <p className="text-sm text-slate-500 mb-2">ملاحظات</p>
                          <p className="text-slate-700">{selectedCase.notes}</p>
                        </div>
                      )}
                    </TabsContent>

                    {/* Archive Tab */}
                    <TabsContent value="archive" className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h3 className="font-semibold">مستندات القضية</h3>
                        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                          <DialogTrigger asChild>
                            <Button size="sm" className="gap-2" data-testid="upload-doc-button">
                              <Upload className="w-4 h-4" />
                              رفع ملف
                            </Button>
                          </DialogTrigger>
                          <DialogContent data-testid="upload-dialog">
                            <DialogHeader>
                              <DialogTitle>رفع مستند للقضية</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleFileUpload} className="space-y-4">
                              <div className="space-y-2">
                                <Label htmlFor="file">الملف *</Label>
                                <Input
                                  id="file"
                                  type="file"
                                  accept="image/*,.pdf,.doc,.docx"
                                  onChange={(e) => setUploadData({ ...uploadData, file: e.target.files[0] })}
                                  required
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="doc-title">عنوان الملف</Label>
                                <Input
                                  id="doc-title"
                                  value={uploadData.title}
                                  onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="doc-description">وصف</Label>
                                <Textarea
                                  id="doc-description"
                                  rows={2}
                                  value={uploadData.description}
                                  onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                                />
                              </div>
                              <div className="flex justify-end gap-3">
                                <Button type="button" variant="outline" onClick={() => setUploadDialogOpen(false)}>
                                  إلغاء
                                </Button>
                                <Button type="submit">رفع</Button>
                              </div>
                            </form>
                          </DialogContent>
                        </Dialog>
                      </div>

                      {documents.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                          <FolderOpen className="w-16 h-16 mx-auto mb-4 opacity-50" />
                          <p>لا توجد مستندات مرفوعة</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 gap-3">
                          {documents.map((doc) => (
                            <div
                              key={doc.id}
                              className="p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                            >
                              <div className="flex items-start gap-3">
                                {getFileIcon(doc.file_type)}
                                <div className="flex-1">
                                  <h4 className="font-semibold text-slate-900">{doc.title}</h4>
                                  {doc.description && (
                                    <p className="text-sm text-slate-600 mt-1">{doc.description}</p>
                                  )}
                                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                                    <span>{formatFileSize(doc.file_size)}</span>
                                    <span>•</span>
                                    <span>{formatDate(doc.created_at)}</span>
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDownload(doc.id, doc.file_name)}
                                  >
                                    <Download className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => handleDeleteDocument(doc.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-16 text-center">
                  <p className="text-slate-500 text-lg">اختر قضية لعرض التفاصيل والأرشيف</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent key={`edit-case-${selectedCase?.id || 'new'}`} className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>تعديل القضية</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEdit} className="space-y-4">
              <CaseFormFields data={formData} setData={setFormData} isEdit={true} />
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  إلغاء
                </Button>
                <Button type="submit">حفظ التعديلات</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CasesNew;
